(function () {
  'use strict';

  /* ── Currency & Money Formatting ────────────────────────────────── */

  var activeCurrency = null;

  function getActiveCurrency() {
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  /* ── HTML Helpers ─────────────────────────────────────────── */

  function escapeHTML(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getSizedImageUrl(url, width) {
    if (!url) return '';
    var separator = url.indexOf('?') !== -1 ? '&' : '?';
    return url + separator + 'width=' + width;
  }

  /* ── DOM ─────────────────────────────────────────────────── */

  function getDrawer() {
    return document.querySelector('.oddit-cart-drawer');
  }

  /* ── Cart API ───────────────────────────────────────────── */

  function getCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) {
      return r.json();
    });
  }

  function addToCart(variantId, quantity, properties) {
    var body = {
      id: Number(variantId),
      quantity: quantity || 1
    };
    if (properties && typeof properties === 'object' && Object.keys(properties).length > 0) {
      body.properties = properties;
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('Add to cart failed: ' + r.status);
      return getCart();
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function addMultipleToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('Add multiple to cart failed: ' + r.status);
      return getCart();
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity: quantity })
    }).then(function (r) {
      if (!r.ok) throw new Error('Update quantity failed: ' + r.status);
      return r.json();
    }).then(function (cart) {
      /* The quantity that just changed may have crossed a tier, so the plan is
         re-picked before anything is drawn — otherwise the drawer would show a
         discount the cart has not earned, and checkout would honour it. */
      return syncSubscriptionTier(cart);
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  /* ── Subscription tiers ───────────────────────────────────────
     A bundle's discount is not a discount at all — it is which Recharge selling
     plan the lines sit on, and each plan is sold as a number of units per
     delivery ("6 products every 1 month at 25%"). The builder picks the right
     plan for the quantity being built, but the cart is a second place quantity
     can change, and nothing re-picked it there. So a shopper could check out on
     the 12-unit plan, return to the cart, drop to 2 units and keep 40% off; or
     add units and stay on a plan well below what they now qualify for.

     After any change to a subscribed line the ladder is re-read and every
     subscribed line is moved onto the plan its combined quantity earns. */

  function readTierLadder() {
    var drawer = getDrawer();
    if (!drawer) return [];
    /* Liquid flattens the setting to "minQty|planId;" segments. */
    var raw = drawer.getAttribute('data-subscription-tiers') || '';
    var tiers = [];
    raw.split(';').forEach(function (segment) {
      var parts = segment.split('|');
      if (parts.length < 2) return;
      var min = parseInt(parts[0], 10);
      var planId = String(parts[1]).trim();
      if (!(min > 0) || !planId) return;
      tiers.push({ min: min, planId: planId });
    });
    /* Highest first, so the first tier a quantity clears is the best it earns. */
    tiers.sort(function (a, b) { return b.min - a.min; });
    return tiers;
  }

  function planIdOf(item) {
    if (!item.selling_plan_allocation || !item.selling_plan_allocation.selling_plan) return null;
    return String(item.selling_plan_allocation.selling_plan.id);
  }

  /* Which plan a combined quantity earns. null means it clears no tier. */
  function planForQuantity(tiers, quantity) {
    for (var i = 0; i < tiers.length; i++) {
      if (quantity >= tiers[i].min) return tiers[i].planId;
    }
    return null;
  }

  function setLineSellingPlan(lineItemKey, sellingPlanId, quantity) {
    /* The quantity has to be restated. /cart/change.js treats an omitted
       quantity as 1 — "If the quantity isn't specified, then it's assumed to
       be 1" — so changing only the plan silently collapses the line to a
       single unit. With a three-flavour bundle that reads as the cart
       dropping to 3 while keeping the discount the larger quantity earned,
       which is the opposite of the leak this is here to close.

       An empty string is how the API is told to drop a selling plan and leave
       the line as a one-off purchase. */
    var body = {
      id: lineItemKey,
      quantity: quantity,
      selling_plan: sellingPlanId === null ? '' : Number(sellingPlanId)
    };
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('Selling plan update failed: ' + r.status);
      return r.json();
    });
  }

  /**
   * Move every subscribed line onto the plan its combined quantity earns.
   * Resolves with the cart to render — unchanged if nothing needed moving.
   */
  function syncSubscriptionTier(cart) {
    var tiers = readTierLadder();
    if (!tiers.length) return Promise.resolve(cart);

    var drawer = getDrawer();
    var belowMin = drawer ? drawer.getAttribute('data-subscription-below-min') : 'one_time';
    var known = {};
    tiers.forEach(function (t) { known[t.planId] = true; });

    function subscribedLines(c) {
      return (c.items || []).filter(function (item) {
        var id = planIdOf(item);
        return id !== null && known[id];
      });
    }

    var lines = subscribedLines(cart);
    if (!lines.length) return Promise.resolve(cart);

    var total = lines.reduce(function (n, item) { return n + item.quantity; }, 0);
    var target = planForQuantity(tiers, total);
    if (target === null && belowMin === 'lowest') {
      target = tiers[tiers.length - 1].planId;
    }

    /* Changing a line's selling plan changes its key, and Shopify may merge two
       lines that become identical — so the cart is re-read each pass rather than
       working from a list captured up front. The pass count is bounded, so a
       plan that will not stick cannot spin forever. */
    var guard = lines.length + 2;

    function pass(current) {
      if (guard-- <= 0) return Promise.resolve(current);
      var next = null;
      var remaining = subscribedLines(current);
      for (var i = 0; i < remaining.length; i++) {
        if (planIdOf(remaining[i]) !== target) { next = remaining[i]; break; }
      }
      if (!next) return Promise.resolve(current);
      return setLineSellingPlan(next.key, target, next.quantity).then(pass);
    }

    return pass(cart).catch(function (err) {
      /* A cart that could not be re-tiered is still a usable cart: show what is
         there rather than leaving the drawer mid-update. */
      if (window.console) console.error('[cart-drawer] tier sync failed', err);
      return cart;
    });
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, selling_plan: Number(sellingPlanId) })
    }).then(function (r) {
      if (!r.ok) throw new Error('Subscription update failed: ' + r.status);
      return r.json();
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function refreshCart() {
    return getCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  /* ── Line Item HTML Builder ───────────────────────────────── */

  function buildLineItemHTML(item, index) {
    var imageHTML = '';
    if (item.image) {
      var imgSrcSmall = getSizedImageUrl(item.image, 90);
      var imgSrcLarge = getSizedImageUrl(item.image, 180);
      var alt = (item.featured_image && item.featured_image.alt)
        ? item.featured_image.alt
        : item.product_title;
      imageHTML =
        '<div class="oddit-cart-drawer__item-image">' +
          '<img' +
            ' src="' + escapeHTML(imgSrcSmall) + '"' +
            ' srcset="' + escapeHTML(imgSrcSmall) + ' 90w, ' + escapeHTML(imgSrcLarge) + ' 180w"' +
            ' sizes="90px"' +
            ' alt="' + escapeHTML(alt) + '"' +
            ' loading="lazy"' +
            ' class="oddit-cart-drawer__item-img"' +
          '>' +
        '</div>';
    }

    var optionsHTML = '';
    if (!item.product_has_only_default_variant && item.options_with_values && item.options_with_values.length) {
      for (var i = 0; i < item.options_with_values.length; i++) {
        var opt = item.options_with_values[i];
        optionsHTML +=
          '<p class="oddit-cart-drawer__item-property">' +
            '<strong>' + escapeHTML(opt.name) + ':</strong> ' + escapeHTML(opt.value) +
          '</p>';
      }
    }

    /* ZAP-101: the subscription's delivery frequency. The drawer rebuilds every
       line in JS and overwrites the server-rendered markup, so this has to be
       emitted here — adding it to the Liquid alone had no effect. Recharge names
       plans "Delivered Every 30 Days (Save 20%)"; the saving already shows in
       the line price, so the parenthetical is trimmed. */
    var planHTML = '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      /* "Delivered Every 30 Days (Save 20%)" -> "Every 30 Days", so it reads as
         a value under a bold "Delivery:" label, matching the Size: line above. */
      var planName = String(item.selling_plan_allocation.selling_plan.name || '')
        .split(' (')[0]
        .replace(/^\s*delivered\s+/i, '')
        .trim();
      if (planName) {
        planHTML =
          '<p class="oddit-cart-drawer__item-property oddit-cart-drawer__item-plan">' +
            '<strong>Delivery:</strong> ' + escapeHTML(planName) +
          '</p>';
      }
    }

    var propsHTML = '';
    if (item.properties) {
      for (var pKey in item.properties) {
        if (!item.properties.hasOwnProperty(pKey)) continue;
        var pVal = item.properties[pKey];
        if (pVal == null || pVal === '' || pKey.charAt(0) === '_') continue;
        var pValStr = String(pVal);
        var pValHTML;
        if (pValStr.indexOf('/uploads/') !== -1) {
          pValHTML = '<a href="' + escapeHTML(pValStr) + '">' + escapeHTML(pValStr.split('/').pop()) + '</a>';
        } else {
          pValHTML = escapeHTML(pValStr);
        }
        propsHTML +=
          '<p class="oddit-cart-drawer__item-property">' +
            '<strong>' + escapeHTML(pKey) + ':</strong> ' + pValHTML +
          '</p>';
      }
    }

    var pricingHTML =
      '<span class="oddit-cart-drawer__item-price" data-item-final-price>' +
        formatMoney(item.final_line_price) +
      '</span>';
    /* Subscription lines report original_line_price === final_line_price, because
       the plan price IS the line price — so the struck-through figure has to come
       from the allocation's compare_at_price instead. Without this a subscription
       showed one price and no saving, while a discounted one-time line showed both. */
    var compareLine = item.original_line_price;
    var alloc = item.selling_plan_allocation;
    var isPlanSaving = false;
    if (
      compareLine === item.final_line_price &&
      alloc &&
      typeof alloc.compare_at_price === 'number' &&
      alloc.compare_at_price > alloc.price
    ) {
      compareLine = alloc.compare_at_price * item.quantity;
      isPlanSaving = true;
    }

    if (compareLine !== item.final_line_price) {
      var savings = compareLine - item.final_line_price;
      /* ZAP-227: plan lines show the percentage, one-time discounts the amount.
         The "(Save 20%)" suffix is stripped off the Recharge plan name above, so
         this badge is the only remaining place the 20% is stated — and it has to
         match what the Liquid renders on first open or the badge would change
         wording the moment a quantity changed. */
      var badgeText = isPlanSaving
        ? 'Save ' + Math.round((savings * 100) / compareLine) + '%'
        : 'Save ' + formatMoney(savings);
      pricingHTML +=
        '<span class="oddit-cart-drawer__item-compare-price">' +
          formatMoney(compareLine) +
        '</span>' +
        '<span class="oddit-cart-drawer__item-save-badge">' +
          badgeText +
        '</span>';
    }

    var escapedTitle = escapeHTML(item.product_title);

    return (
      '<div class="oddit-cart-drawer__line-item" data-cart-item' +
        ' data-line-item-key="' + escapeHTML(item.key) + '"' +
        ' data-line-item-index="' + index + '">' +
        imageHTML +
        '<div class="oddit-cart-drawer__item-details">' +
          '<div class="oddit-cart-drawer__item-info">' +
            '<div class="oddit-cart-drawer__item-meta">' +
              '<h3 class="oddit-cart-drawer__item-title">' +
                '<a href="' + escapeHTML(item.url) + '">' + escapedTitle + '</a>' +
              '</h3>' +
              optionsHTML +
              planHTML +
              propsHTML +
            '</div>' +
            '<div class="oddit-cart-drawer__item-pricing">' +
              pricingHTML +
            '</div>' +
          '</div>' +
          '<div class="oddit-cart-drawer__item-actions">' +
            '<div class="oddit-cart-drawer__quantity">' +
              '<button type="button" class="oddit-cart-drawer__quantity-btn"' +
                ' data-quantity-minus' +
                ' aria-label="Decrease quantity for ' + escapedTitle + '">' +
                '<span class="oddit-cart-drawer__quantity-icon--minus" aria-hidden="true"></span>' +
              '</button>' +
              '<span class="oddit-cart-drawer__quantity-value" data-quantity-value>' +
                item.quantity +
              '</span>' +
              '<button type="button" class="oddit-cart-drawer__quantity-btn"' +
                ' data-quantity-plus' +
                ' aria-label="Increase quantity for ' + escapedTitle + '">' +
                '<span class="oddit-cart-drawer__quantity-icon--plus" aria-hidden="true"></span>' +
              '</button>' +
            '</div>' +
            '<button type="button" class="oddit-cart-drawer__item-remove"' +
              ' data-remove-item' +
              ' aria-label="Remove ' + escapedTitle + '">' +
              'Remove' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  /* ── Progress Bar ─────────────────────────────────────────── */

  function updateProgressBar(cart) {
    var drawer = getDrawer();
    if (!drawer) return;

    var threshold = parseFloat(drawer.getAttribute('data-free-shipping-threshold')) || 0;
    if (threshold <= 0) return;

    /* Rounded, because the threshold arrives as dollars and a plain multiply
       leaves float dust on values like 4.9 — enough for "away from free
       shipping" to survive a cart that has already met it. */
    var thresholdCents = Math.round(threshold * 100);
    var cartTotalCents = cart.total_price;
    var percentage = Math.min((cartTotalCents / thresholdCents) * 100, 100);
    var remainingCents = Math.max(thresholdCents - cartTotalCents, 0);

    var progressFills = drawer.querySelectorAll('[data-progress-fill]');
    for (var i = 0; i < progressFills.length; i++) {
      progressFills[i].style.width = percentage + '%';
      if (percentage >= 100) {
        progressFills[i].classList.add('oddit-cart-drawer__progress-fill--full');
      } else {
        progressFills[i].classList.remove('oddit-cart-drawer__progress-fill--full');
      }
    }

    var progressEl = drawer.querySelector('[data-cart-progress]');
    var awayTemplate = (progressEl && progressEl.getAttribute('data-away-text'))
      || 'You are <strong>[amount]</strong> away from <strong>FREE shipping</strong>';
    var reachedTemplate = (progressEl && progressEl.getAttribute('data-reached-text'))
      || 'You\'ve unlocked <strong>free shipping!</strong>';

    var progressTexts = drawer.querySelectorAll('[data-progress-text]');
    for (var j = 0; j < progressTexts.length; j++) {
      if (remainingCents > 0) {
        progressTexts[j].innerHTML = awayTemplate.replace('[amount]', formatMoney(remainingCents));
      } else {
        progressTexts[j].innerHTML = reachedTemplate;
      }
    }

    var remainingEls = drawer.querySelectorAll('.oddit-cart-drawer__progress-remaining');
    for (var k = 0; k < remainingEls.length; k++) {
      remainingEls[k].style.display = percentage >= 100 ? 'none' : '';
    }
  }

  /* ── Upsell Filtering ────────────────────────────────────── */

  function filterUpsells(cart) {
    var drawer = getDrawer();
    if (!drawer) return;

    var cartProductIds = [];
    for (var i = 0; i < cart.items.length; i++) {
      cartProductIds.push(cart.items[i].product_id);
    }

    var upsellCards = drawer.querySelectorAll('[data-upsell-card]');
    for (var j = 0; j < upsellCards.length; j++) {
      var productId = parseInt(upsellCards[j].getAttribute('data-product-id'), 10);
      upsellCards[j].style.display =
        cartProductIds.indexOf(productId) !== -1 ? 'none' : '';
    }

    var crossSellCards = drawer.querySelectorAll('[data-cross-sell-card]');
    for (var k = 0; k < crossSellCards.length; k++) {
      var csProductId = parseInt(crossSellCards[k].getAttribute('data-product-id'), 10);
      crossSellCards[k].style.display =
        cartProductIds.indexOf(csProductId) !== -1 ? 'none' : '';
    }
  }

  /* ── Cross-Sell Recommendations (Section Rendering API) ───── */

  function renderCrossSell(cart) {
    var drawer = getDrawer();
    if (!drawer) return;

    var container = drawer.querySelector('[data-cross-sell-container]');
    if (!container) return;

    var source = container.getAttribute('data-cross-sell-source');
    if (source !== 'recommendations') return;

    var intent = container.getAttribute('data-cross-sell-intent') || 'complementary';
    var limit = parseInt(container.getAttribute('data-cross-sell-limit'), 10) || 4;
    var productsContainer = container.querySelector('[data-cross-sell-products]');
    if (!productsContainer) return;

    var cartProductIds = [];
    for (var i = 0; i < cart.items.length; i++) {
      cartProductIds.push(cart.items[i].product_id);
    }

    if (cartProductIds.length === 0) {
      productsContainer.innerHTML = '';
      return;
    }

    var referenceProductId = cartProductIds[cartProductIds.length - 1];
    var sectionEl = drawer.closest('.shopify-section');
    var sectionId = sectionEl
      ? sectionEl.id.replace('shopify-section-', '')
      : '';

    var url =
      '/recommendations/products' +
      '?section_id=' + encodeURIComponent(sectionId) +
      '&product_id=' + encodeURIComponent(referenceProductId) +
      '&limit=' + encodeURIComponent(limit) +
      '&intent=' + encodeURIComponent(intent);

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var freshProducts = doc.querySelector('[data-cross-sell-products]');
        if (freshProducts) {
          productsContainer.innerHTML = freshProducts.innerHTML;
          var newCards = productsContainer.querySelectorAll('[data-cross-sell-card]');
          for (var n = 0; n < newCards.length; n++) {
            var pid = parseInt(newCards[n].getAttribute('data-product-id'), 10);
            if (cartProductIds.indexOf(pid) !== -1) {
              newCards[n].remove();
            }
          }
        }
      })
      .catch(function () {});
  }

  /* ── Render Orchestrator ──────────────────────────────────── */

  function renderCart(cart) {
    var drawer = getDrawer();
    if (!drawer) return;

    if (cart && cart.currency) {
      activeCurrency = cart.currency;
    }

    var itemsContainers = drawer.querySelectorAll('[data-cart-items]');
    var lineItemsHTML = '';
    for (var i = 0; i < cart.items.length; i++) {
      lineItemsHTML += buildLineItemHTML(cart.items[i], i + 1);
    }
    for (var j = 0; j < itemsContainers.length; j++) {
      itemsContainers[j].innerHTML = lineItemsHTML;
    }

    var countEls = drawer.querySelectorAll('[data-cart-count]');
    for (var c = 0; c < countEls.length; c++) {
      countEls[c].textContent = cart.item_count;
    }

    var subtotalEls = drawer.querySelectorAll('[data-cart-subtotal]');
    for (var s = 0; s < subtotalEls.length; s++) {
      subtotalEls[s].textContent = formatMoney(cart.total_price);
    }

    updateProgressBar(cart);

    var filledEls = drawer.querySelectorAll('[data-cart-filled]');
    var emptyEls = drawer.querySelectorAll('[data-cart-empty]');
    var footerEl = drawer.querySelector('[data-cart-footer]');
    var checkoutBtn = drawer.querySelector('.oddit-cart-drawer__checkout');

    if (cart.item_count === 0) {
      drawer.classList.add('is-empty');
      for (var fe = 0; fe < filledEls.length; fe++) {
        filledEls[fe].style.display = 'none';
      }
      for (var ee = 0; ee < emptyEls.length; ee++) {
        emptyEls[ee].style.display = '';
      }
      if (footerEl) footerEl.style.display = 'none';
      if (checkoutBtn) checkoutBtn.disabled = true;
    } else {
      drawer.classList.remove('is-empty');
      for (var ff = 0; ff < filledEls.length; ff++) {
        filledEls[ff].style.display = '';
      }
      for (var ef = 0; ef < emptyEls.length; ef++) {
        emptyEls[ef].style.display = 'none';
      }
      if (footerEl) footerEl.style.display = '';
      if (checkoutBtn) checkoutBtn.disabled = false;
    }

    filterUpsells(cart);

    renderCrossSell(cart);

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  /* ── UI: Open / Close / Toggle ────────────────────────────── */

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.style.removeProperty('display');
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var panel = drawer.querySelector('.oddit-cart-drawer__panel');
    if (panel) panel.focus();
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* Opening because something was added, which the merchant can switch off,
     as opposed to opening because the shopper asked — the cart icon calls
     openDrawer directly and is unaffected. */
  function openAfterAdd() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (drawer.getAttribute('data-open-on-add') !== 'true') return;
    openDrawer();
  }

  function toggleDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (drawer.classList.contains('is-open')) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }

  /* ── Add-to-Cart Handler ──────────────────────────────────── */

  function handleAddToCart(btn) {
    if (btn.classList.contains('is-loading')) return;

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    var quantity = Number(btn.getAttribute('data-quantity')) || 1;

    var restWidth = btn.getBoundingClientRect().width;
    btn.style.minWidth = restWidth + 'px';

    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    addToCart(Number(variantId), quantity)
      .then(function () {
        openAfterAdd();
      })
      .finally(function () {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
        btn.style.minWidth = '';
      });
  }

  /* ── Init & Event Delegation ──────────────────────────────── */

  var keydownBound = false;

  function init() {
    var drawer = getDrawer();
    if (!drawer) return;

    /* Variant picker — keep the sibling add button pointed at the chosen
       variant, and disable it when that variant is sold out. */
    drawer.addEventListener('change', function (e) {
      var select = e.target.closest && e.target.closest('[data-variant-select]');
      if (!select) return;

      var card = select.closest('[data-cross-sell-card], [data-upsell-card]') || select.parentElement.parentElement;
      var addBtn = card ? card.querySelector('[data-add-to-cart]') : null;
      if (!addBtn) return;

      addBtn.setAttribute('data-variant-id', select.value);

      var opt = select.options[select.selectedIndex];
      var available = !opt || opt.getAttribute('data-available') !== 'false';
      addBtn.disabled = !available;
    });

    drawer.addEventListener('click', function (e) {
      var target = e.target;

      /* ZAP-191: close on any click that lands outside the panel, not only on
         the overlay element. The overlay was being hidden by base.css's
         `div:empty` rule, so it had no box to hit and this never fired. The
         CSS side is fixed too, but testing the panel directly means the
         behaviour no longer depends on the backdrop having geometry — this
         listener is bound on the drawer root, which only covers the screen
         while the drawer is open. */
      if (
        target.matches('[data-cart-overlay]') ||
        target.closest('[data-cart-overlay]') ||
        !target.closest('.oddit-cart-drawer__panel')
      ) {
        closeDrawer();
        return;
      }

      if (target.closest('[data-cart-close]')) {
        closeDrawer();
        if (!target.closest('a[href]')) {
          e.preventDefault();
        }
        return;
      }

      var minusBtn = target.closest('[data-quantity-minus]');
      if (minusBtn) {
        e.preventDefault();
        var lineItemMinus = minusBtn.closest('[data-cart-item]');
        if (lineItemMinus) {
          var keyMinus = lineItemMinus.getAttribute('data-line-item-key');
          var qtyElMinus = lineItemMinus.querySelector('[data-quantity-value]');
          var currentQtyMinus = qtyElMinus ? parseInt(qtyElMinus.textContent, 10) : 1;
          if (currentQtyMinus <= 1) {
            removeFromCart(keyMinus);
          } else {
            updateLineItemQuantity(keyMinus, currentQtyMinus - 1);
          }
        }
        return;
      }

      var plusBtn = target.closest('[data-quantity-plus]');
      if (plusBtn) {
        e.preventDefault();
        var lineItemPlus = plusBtn.closest('[data-cart-item]');
        if (lineItemPlus) {
          var keyPlus = lineItemPlus.getAttribute('data-line-item-key');
          var qtyElPlus = lineItemPlus.querySelector('[data-quantity-value]');
          var currentQtyPlus = qtyElPlus ? parseInt(qtyElPlus.textContent, 10) : 1;
          updateLineItemQuantity(keyPlus, currentQtyPlus + 1);
        }
        return;
      }

      var removeBtn = target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        var lineItemRemove = removeBtn.closest('[data-cart-item]');
        if (lineItemRemove) {
          var keyRemove = lineItemRemove.getAttribute('data-line-item-key');
          removeFromCart(keyRemove);
        }
        return;
      }

      var addBtn = target.closest('[data-add-to-cart]');
      if (addBtn) {
        e.preventDefault();
        handleAddToCart(addBtn);
        return;
      }
    });

    if (!keydownBound) {
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          var d = getDrawer();
          if (d && d.classList.contains('is-open')) {
            closeDrawer();
          }
        }
      });
      keydownBound = true;
    }
  }

  /* ── Public API ───────────────────────────────────────────── */

  window.OdditCartDrawer = {
    open: openDrawer,
    openAfterAdd: openAfterAdd,
    close: closeDrawer,
    toggle: toggleDrawer,
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart
  };

  /* ── Boot ───────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('.oddit-cart-drawer')) {
      init();
    }
  });
})();

/* ── Theme Editor: Preserve drawer state across section reloads ── */

(function () {
  if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

  var STATE_KEY = 'oddit-cart-drawer-editor-open';

  function findDrawerIn(node) {
    if (!node || typeof node.querySelector !== 'function') return null;
    if (node.matches && node.matches('.oddit-cart-drawer')) return node;
    return node.querySelector('.oddit-cart-drawer');
  }

  document.addEventListener('shopify:section:unload', function (event) {
    var drawer = findDrawerIn(event.target);
    if (!drawer) return;
    var isOpen = drawer.classList.contains('is-open');
    try {
      sessionStorage.setItem(STATE_KEY, isOpen ? '1' : '0');
    } catch (e) {}
  });

  document.addEventListener('shopify:section:load', function (event) {
    var drawer = findDrawerIn(event.target);
    if (!drawer) return;
    var wasOpen;
    try {
      wasOpen = sessionStorage.getItem(STATE_KEY) === '1';
    } catch (e) {
      wasOpen = false;
    }
    if (!wasOpen) return;
    requestAnimationFrame(function () {
      if (window.OdditCartDrawer && typeof window.OdditCartDrawer.open === 'function') {
        window.OdditCartDrawer.open();
      }
    });
  });

  document.addEventListener('shopify:section:select', function (event) {
    var drawer = findDrawerIn(event.target);
    if (!drawer) return;
    if (window.OdditCartDrawer && typeof window.OdditCartDrawer.open === 'function') {
      window.OdditCartDrawer.open();
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var sectionEl = event.target && event.target.closest && event.target.closest('.shopify-section');
    var drawer = findDrawerIn(sectionEl) || findDrawerIn(event.target);
    if (!drawer) return;
    if (window.OdditCartDrawer && typeof window.OdditCartDrawer.open === 'function') {
      window.OdditCartDrawer.open();
    }
  });
})();